'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  updateMyProfileSchema,
  useUpdateMyInvestorProfile,
  type InvestorMe,
  type UpdateMyProfileFormValues,
} from '@/features/investors'

export function EditProfileDialog({
  open,
  onOpenChange,
  profile,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  profile: InvestorMe
}) {
  const updateProfile = useUpdateMyInvestorProfile()
  const form = useForm<UpdateMyProfileFormValues>({
    resolver: zodResolver(updateMyProfileSchema),
    mode: 'onTouched',
    values: { name: profile.name ?? '', city: profile.city ?? '' },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await updateProfile.mutateAsync(values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
            <DialogDescription>Update the details we have on file for you.</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="profile-name">Full name</FieldLabel>
              <Input id="profile-name" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="profile-city">City</FieldLabel>
              <Input id="profile-city" {...form.register('city')} />
            </Field>
            <div className="flex flex-col gap-1.5 text-sm">
              <p className="text-muted-foreground">
                Email: <span className="text-foreground">{profile.email ?? '—'}</span>
              </p>
              <p className="text-muted-foreground text-xs">Contact support to change this.</p>
            </div>
            <div className="flex flex-col gap-1.5 text-sm">
              <p className="text-muted-foreground">
                Mobile: <span className="text-foreground">+91 {profile.mobile}</span>
              </p>
              <p className="text-muted-foreground text-xs">Contact support to change this.</p>
            </div>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
